import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { MapPin, Navigation } from 'lucide-react';

interface SatelliteMapPickerProps {
  latitude: number | null;
  longitude: number | null;
  radiusMeters: number;
  onChange?: (coords: { latitude: number; longitude: number }) => void;
  height?: string;
  readOnly?: boolean;
  studentLocation?: { latitude: number; longitude: number } | null;
}

export const SatelliteMapPicker: React.FC<SatelliteMapPickerProps> = ({
  latitude,
  longitude,
  radiusMeters,
  onChange,
  height = '320px',
  readOnly = false,
  studentLocation = null,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const studentMarkerRef = useRef<any>(null);
  const [isLeafletReady, setIsLeafletReady] = useState(false);

  // Default to Kamphaeng Phet Rajabhat University if null
  const defaultLat = 16.48278;
  const defaultLng = 99.52222;

  const currentLat = latitude !== null && latitude !== undefined ? Number(latitude) : defaultLat;
  const currentLng = longitude !== null && longitude !== undefined ? Number(longitude) : defaultLng;

  // 1. Ensure Leaflet CSS & JS are loaded dynamically
  useEffect(() => {
    if ((window as any).L) {
      setIsLeafletReady(true);
      return;
    }

    const cssId = 'leaflet-css-cdn';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    const scriptId = 'leaflet-js-cdn';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.async = true;
      script.onload = () => {
        setIsLeafletReady(true);
      };
      document.body.appendChild(script);
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).L) {
          setIsLeafletReady(true);
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }
  }, []);

  // 2. Initialize Map once Leaflet is ready
  useEffect(() => {
    if (!isLeafletReady || !mapContainerRef.current) return;
    const L = (window as any).L;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [currentLat, currentLng],
        zoom: 16,
        zoomControl: true,
      });

      // Google Hybrid Satellite Tile Layer with road & landmark labels
      L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
        maxZoom: 20,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        attribution: '&copy; Google Satellite Maps',
      }).addTo(map);

      // Default Pin Icon
      const examIcon = L.divIcon({
        className: 'custom-exam-marker',
        html: `
          <div style="background-color: #2563EB; width: 32px; height: 32px; border-radius: 50%; border: 3px solid #FFFFFF; box-shadow: 0 4px 10px rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; color: white;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      });

      // Exam Center Marker
      const marker = L.marker([currentLat, currentLng], {
        draggable: !readOnly,
        icon: examIcon,
      }).addTo(map);
      marker.bindPopup('<b>จุดศูนย์กลางสถานที่สอบ</b><br/>คลิกหรือลากหมุดเพื่อปรับพิกัด');
      markerRef.current = marker;

      // Geofence Radius Circle
      const circle = L.circle([currentLat, currentLng], {
        radius: radiusMeters || 500,
        color: '#2563EB',
        weight: 2,
        fillColor: '#3B82F6',
        fillOpacity: 0.2,
      }).addTo(map);
      circleRef.current = circle;

      // Click on map to relocate marker
      if (!readOnly && onChange) {
        map.on('click', (e: any) => {
          const { lat, lng } = e.latlng;
          marker.setLatLng([lat, lng]);
          circle.setLatLng([lat, lng]);
          onChange({
            latitude: Number(lat.toFixed(7)),
            longitude: Number(lng.toFixed(7)),
          });
        });

        marker.on('dragend', () => {
          const latLng = marker.getLatLng();
          circle.setLatLng(latLng);
          onChange({
            latitude: Number(latLng.lat.toFixed(7)),
            longitude: Number(latLng.lng.toFixed(7)),
          });
        });
      }

      mapInstanceRef.current = map;
    } else {
      // Update center & circle if already instantiated
      if (markerRef.current) {
        markerRef.current.setLatLng([currentLat, currentLng]);
      }
      if (circleRef.current) {
        circleRef.current.setLatLng([currentLat, currentLng]);
        circleRef.current.setRadius(radiusMeters || 500);
      }
    }
  }, [isLeafletReady]);

  // Update position or radius changes
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([currentLat, currentLng]);
    }
    if (circleRef.current) {
      circleRef.current.setLatLng([currentLat, currentLng]);
      circleRef.current.setRadius(radiusMeters || 500);
    }
    mapInstanceRef.current.panTo([currentLat, currentLng]);
  }, [currentLat, currentLng, radiusMeters]);

  // Handle student location display if in portal
  useEffect(() => {
    if (!mapInstanceRef.current || !studentLocation) return;
    const L = (window as any).L;
    if (!L) return;

    if (!studentMarkerRef.current) {
      const studentIcon = L.divIcon({
        className: 'custom-student-marker',
        html: `
          <div style="background-color: #EF4444; width: 28px; height: 28px; border-radius: 50%; border: 3px solid #FFFFFF; box-shadow: 0 4px 10px rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; color: white;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="3"></circle></svg>
          </div>
        `,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });

      const sMarker = L.marker([studentLocation.latitude, studentLocation.longitude], {
        icon: studentIcon,
      }).addTo(mapInstanceRef.current);
      sMarker.bindPopup('<b>ตำแหน่งอุปกรณ์ของคุณ</b>');
      studentMarkerRef.current = sMarker;
    } else {
      studentMarkerRef.current.setLatLng([studentLocation.latitude, studentLocation.longitude]);
    }
  }, [studentLocation]);

  const handlePresetKPRU = () => {
    if (!onChange) return;
    onChange({ latitude: 16.48278, longitude: 99.52222 });
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('เบราว์เซอร์ของคุณไม่รองรับการตรวจจับพิกัด GPS');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (onChange) {
          onChange({
            latitude: Number(pos.coords.latitude.toFixed(7)),
            longitude: Number(pos.coords.longitude.toFixed(7)),
          });
        }
      },
      (err) => {
        alert('ไม่สามารถดึงตำแหน่งปัจจุบันได้: ' + err.message);
      },
      { enableHighAccuracy: true }
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MapPin size={14} className="text-blue-600" />
            คลิกบนแผนที่ดาวเทียมเพื่อปักหมุด หรือเลือกพิกัดแนะนำ:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Navigation size={13} />}
              onClick={handleGetCurrentLocation}
              sx={{ fontSize: '0.75rem', py: 0.3 }}
            >
              ใช้พิกัดปัจจุบัน
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePresetKPRU}
              sx={{ fontSize: '0.75rem', py: 0.3 }}
            >
              มรภ.กำแพงเพชร (อาคาร 1)
            </Button>
          </Box>
        </Box>
      )}

      {/* Map Canvas */}
      <Box
        ref={mapContainerRef}
        sx={{
          height,
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #CBD5E1',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          position: 'relative',
        }}
      />

      {/* Map Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.8, px: 0.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            size="small"
            label={`พิกัด: ${currentLat.toFixed(5)}, ${currentLng.toFixed(5)}`}
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22 }}
          />
          <Chip
            size="small"
            label={`รัศมี: ${radiusMeters} เมตร`}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22, fontWeight: 600 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          📡 ภาพถ่ายดาวเทียมความละเอียดสูง (Google Hybrid)
        </Typography>
      </Box>
    </Box>
  );
};
